import { OktaAuth } from "@okta/okta-auth-js";
import { loadShellEnv, type ShellEnv } from "../env.js";

/**
 * Okta SDK configuration for the shell, isolated from the React component so the security-critical
 * settings can be imported and asserted directly in tests without rendering.
 *
 * Security posture (auth.md §5, frontend-security.md §5.1):
 *  - PKCE flow (`pkce: true`) — no client secret in the browser.
 *  - `tokenManager.storage = "memory"` — the ID/access tokens live in JS memory ONLY. They are
 *    NEVER written to localStorage or sessionStorage, so an XSS that gains script execution cannot
 *    dump them. A page reload re-acquires tokens via the Okta session / backend refresh-cookie path.
 *  - `services.autoRenew` is disabled because renewal is driven by the backend
 *    `__Host-aire-refresh` cookie (I03-2), not by Okta auto-renew writing to storage.
 *
 * One OktaAuth instance backs the WHOLE platform now: because the shell is a single document, close,
 * admin, and every future module share this in-memory token manager. The admin SPA's former silent
 * token-acquisition dance (`getWithoutPrompt` on a fresh document) is gone — there is no second
 * document to bootstrap.
 */
export const OKTA_TOKEN_STORAGE = "memory" as const;

export function createOktaAuth(env: ShellEnv = loadShellEnv()): OktaAuth {
  // Security still needs an SDK instance in local-auth mode, but no token or request from this
  // placeholder issuer is trusted. `useAuthContext` supplies the synthetic dev identity only when
  // Vite proves this is a local development build; production builds reject the flag in env.ts.
  const issuer = env.localAuthEnabled ? "http://localhost:8081/oauth2/default" : env.oktaIssuer;
  const clientId = env.localAuthEnabled ? "aire-local-development" : env.oktaClientId;
  const redirectUri = env.localAuthEnabled
    ? `${window.location.origin}/auth/callback`
    : env.oktaRedirectUri;

  return new OktaAuth({
    issuer,
    clientId,
    redirectUri,
    scopes: ["openid", "profile", "email", "groups"],
    pkce: true,
    tokenManager: {
      // In-memory storage only — the security-critical line for the platform.
      storage: OKTA_TOKEN_STORAGE,
    },
    services: {
      autoRenew: false,
      autoRemove: true,
    },
  });
}
